import React from "react";
import { motion } from "framer-motion";
import ParticleField from "@/components/site/ParticleField";
import { HRLogoFull } from "@/components/site/Logo";
import { StudioIcon, MediaIcon, GrowthIcon, FmcgIcon } from "@/components/site/DivisionIcons";
import { ArrowUpRight, Phone, Sparkles, MoveDown } from "lucide-react";
import { HERO } from "@/constants/testIds";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const rise = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] } },
};

/* Orbital ecosystem — HR ENTERPRISES core + 4 division satellites */
const ORBIT = [
  { icon: StudioIcon, label: "STUDIO", angle: 0 },
  { icon: MediaIcon, label: "MEDIA", angle: 90 },
  { icon: GrowthIcon, label: "GROWTH", angle: 180 },
  { icon: FmcgIcon, label: "FMCG", angle: 270 },
];

const EcosystemVisual = () => {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[520px]">
      {/* Ambient gold aura */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.22),transparent_65%)] blur-2xl" />

      {/* Rotating outer ring */}
      <motion.div
        aria-hidden
        animate={{ rotate: 360 }}
        transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 rounded-full border border-[var(--gold)]/15"
      />
      {/* Counter-rotating inner ring */}
      <motion.div
        aria-hidden
        animate={{ rotate: -360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        className="absolute inset-16 rounded-full border border-[var(--gold)]/10"
      />

      {/* Static dashed mid ring */}
      <div
        aria-hidden
        className="absolute inset-8 rounded-full border border-dashed border-white/[0.06]"
      />

      {/* Central HR logo */}
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-1/2 top-1/2 flex h-[46%] w-[46%] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
      >
        <div className="relative h-full w-full">
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.35),transparent_60%)] blur-xl" />
          <HRLogoFull className="relative h-full w-full rounded-full border border-[var(--gold)]/25 object-cover shadow-[0_30px_90px_-30px_rgba(212,175,55,0.55)]" />
        </div>
      </motion.div>

      {/* Division satellite nodes — position on orbit via absolute + trigonometry via CSS */}
      <div className="absolute inset-0">
        {ORBIT.map((o, idx) => {
          const rad = (o.angle * Math.PI) / 180;
          const radius = 46; // % of container
          const x = 50 + radius * Math.cos(rad);
          const y = 50 + radius * Math.sin(rad);
          const Icon = o.icon;
          return (
            <motion.div
              key={o.label}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.4 + idx * 0.12,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
              }}
              className="absolute"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 4 + idx * 0.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: idx * 0.4,
                }}
                className="group flex flex-col items-center gap-1.5"
              >
                <div className="grid h-14 w-14 place-items-center rounded-2xl border border-[var(--gold)]/30 bg-[#0e0e10]/95 text-[var(--gold-bright)] shadow-[0_12px_30px_-12px_rgba(212,175,55,0.4)] backdrop-blur-md transition-all duration-500 hover:border-[var(--gold)]/60 hover:bg-[var(--gold)]/10">
                  <Icon className="h-6 w-6" />
                </div>
                <span
                  className="font-mono-label text-[9px] text-zinc-400"
                  style={{ letterSpacing: "0.28em" }}
                >
                  {o.label}
                </span>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Floating gold accent cubes */}
      <motion.div
        aria-hidden
        animate={{ y: [0, -14, 0], rotate: [0, 45, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-4 top-4 h-6 w-6 rounded-md border border-[var(--gold)]/40 bg-gradient-to-br from-[var(--gold)]/25 to-transparent"
      />
      <motion.div
        aria-hidden
        animate={{ y: [0, 12, 0], rotate: [0, -30, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-6 left-2 h-4 w-4 rotate-45 border border-[var(--gold)]/40 bg-[var(--gold)]/10"
      />
    </div>
  );
};

const Hero = () => {
  return (
    <section
      id="home"
      data-testid={HERO.root}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#060606]"
    >
      <div className="aurix-aura" />
      <div className="absolute inset-0">
        <ParticleField density={70} />
      </div>

      {/* Vertical grid guides */}
      <div className="pointer-events-none absolute inset-y-0 left-6 right-6 hidden lg:block">
        <div className="mx-auto grid h-full max-w-[1400px] grid-cols-12 gap-6">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-full border-l border-white/[0.04]" />
          ))}
        </div>
      </div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-6 pb-24 pt-32 lg:px-10">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-12 items-center gap-8"
        >
          <div className="col-span-12 lg:col-span-7">
            <motion.div
              variants={rise}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5"
            >
              <Sparkles className="h-3.5 w-3.5 text-[var(--gold)]" />
              <span className="font-mono-label text-zinc-400">
                HR ENTERPRISES · <span className="text-white">Building The Next Generation</span>
              </span>
            </motion.div>

            <motion.h1
              variants={rise}
              data-testid={HERO.headline}
              className="font-display text-[52px] leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-[84px]"
            >
              Don&apos;t just{" "}
              <span className="gold-gradient-text">launch.</span>
              <br />
              Leave a{" "}
              <span className="font-serif-display italic text-zinc-100">
                legacy.
              </span>
            </motion.h1>

            <motion.p
              variants={rise}
              className="mt-7 max-w-lg text-[16px] leading-relaxed text-zinc-400 sm:text-[17px]"
            >
              We build brands, websites, apps, and growth engines that
              don&apos;t just look good — they{" "}
              <span className="text-white">drive real business.</span>
            </motion.p>

            <motion.div variants={rise} className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid={HERO.primaryCta}
                className="btn-gold"
              >
                Build With Us
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
              </a>
              <a
                href="#contact"
                data-testid={HERO.secondaryCta}
                className="btn-ghost"
              >
                <Phone className="h-4 w-4" strokeWidth={1.8} />
                Book a Strategy Call
              </a>
            </motion.div>

            <motion.div
              variants={rise}
              className="mt-14 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4"
            >
              {[
                { k: "Active Projects", v: "05" },
                { k: "Industries", v: "05" },
                { k: "Launch Promise", v: "48h" },
                { k: "Commitment", v: "100%" },
              ].map((s) => (
                <div key={s.k} className="border-l border-white/[0.08] pl-4">
                  <div className="font-display text-2xl font-semibold text-white">
                    {s.v}
                  </div>
                  <div className="font-mono-label mt-1 text-zinc-500">
                    {s.k}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — ecosystem orbital visual */}
          <motion.div
            variants={rise}
            className="col-span-12 lg:col-span-5"
          >
            <EcosystemVisual />
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#stats"
        data-testid={HERO.scrollHint}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 flex-col items-center gap-2 hidden sm:flex"
      >
        <span className="font-mono-label text-zinc-500">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="text-[var(--gold)]"
        >
          <MoveDown className="h-4 w-4" />
        </motion.div>
      </motion.a>
    </section>
  );
};

export default Hero;
